/**
 * Player tier (檔次 / 段位) by Elo rating — the single source of truth.
 *
 * v2.4 FDT-O-010 — extracted so the solve screen, practice star reveal and the
 * share card all surface the SAME tier (DRY; previously only g5-share had a
 * local copy that referenced the wrong `streak.*` i18n namespace — fixed here
 * to the real `segment.*` keys).
 *
 * Rating starts at 1000 (defaultProfile) and is updated server-side per Daily
 * solve (worker /v1/score returns the new absolute rating); the client mirrors
 * it into profile.rating so the tier reflects real standing.
 */
export interface Tier {
  /** Inclusive lower rating bound. */
  min: number;
  /** Inline glyph (color-blind-safe shape, paired with the i18n label). */
  glyph: string;
  /** i18n key under `segment.*`. */
  key: string;
}

/** Highest first so `find(r >= min)` returns the top tier the rating qualifies for. */
export const TIERS: readonly Tier[] = [
  { min: 1900, glyph: '✦', key: 'segment.master' },
  { min: 1700, glyph: '✦', key: 'segment.diamond' },
  { min: 1500, glyph: '★', key: 'segment.platinum' },
  { min: 1300, glyph: '▲', key: 'segment.gold' },
  { min: 1100, glyph: '◆', key: 'segment.silver' },
  { min: 0, glyph: '●', key: 'segment.bronze' },
] as const;

/** The tier a rating currently sits in. Never null (bronze floors at 0). */
export function tierFor(rating: number): Tier {
  return TIERS.find((t) => rating >= t.min) ?? TIERS[TIERS.length - 1]!;
}

/**
 * Rating points to the next tier up, or null at the top tier. Used to show a
 * "X to next tier" nudge on the solve screen.
 */
export function pointsToNextTier(rating: number): number | null {
  const higher = TIERS.filter((t) => t.min > rating).map((t) => t.min);
  if (higher.length === 0) return null;
  return Math.min(...higher) - rating;
}

/**
 * The tier immediately above the current rating (the one you're climbing
 * toward), or null at the top tier. Pairs with pointsToNextTier for a concrete
 * "N pts to <next tier>" nudge.
 */
export function nextTier(rating: number): Tier | null {
  const higher = TIERS.filter((t) => t.min > rating).sort((a, b) => a.min - b.min);
  return higher[0] ?? null;
}
